import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { InnovriddhiMark } from "./InnovriddhiMark";

export const Navbar: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    if (!isHome) {
      window.location.href = `/${id}`;
      return;
    }
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-200 ${
        scrolled
          ? "bg-canvas/90 backdrop-blur-md shadow-sm border-b border-charcoal/10 py-3"
          : "bg-canvas/60 backdrop-blur-sm py-4 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Brand Lockup */}
        <Link
          to="/"
          className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-plum rounded-md"
        >
          <div className="text-plum group-hover:scale-105 transition-transform duration-200">
            <InnovriddhiMark size={34} form="interlaced" />
          </div>
          <span className="font-brand text-2xl font-bold tracking-tight text-ink">
            Innovriddhi
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-ink-secondary">
          <button
            onClick={() => scrollToSection("#products")}
            className="hover:text-plum transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-plum rounded"
          >
            Products
          </button>
          <button
            onClick={() => scrollToSection("#principles")}
            className="hover:text-plum transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-plum rounded"
          >
            Philosophy
          </button>
          <button
            onClick={() => scrollToSection("#about")}
            className="hover:text-plum transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-plum rounded"
          >
            About
          </button>
          <button
            onClick={() => scrollToSection("#contact")}
            className="hover:text-plum transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-plum rounded"
          >
            Contact
          </button>
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={() => scrollToSection("#contact")}
            className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white bg-plum hover:bg-plum-hover transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-plum"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile menu trigger */}
        <div className="md:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-ink-secondary hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-plum rounded-lg"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden px-4 pt-3 pb-6 border-b border-charcoal/10 bg-canvas/95 backdrop-blur-xl space-y-3">
          <button
            onClick={() => scrollToSection("#products")}
            className="block w-full text-left px-3 py-2.5 rounded-lg text-base font-medium text-ink hover:bg-cream-300/40"
          >
            Products
          </button>
          <button
            onClick={() => scrollToSection("#principles")}
            className="block w-full text-left px-3 py-2.5 rounded-lg text-base font-medium text-ink hover:bg-cream-300/40"
          >
            Philosophy
          </button>
          <button
            onClick={() => scrollToSection("#about")}
            className="block w-full text-left px-3 py-2.5 rounded-lg text-base font-medium text-ink hover:bg-cream-300/40"
          >
            About
          </button>
          <button
            onClick={() => scrollToSection("#contact")}
            className="block w-full text-left px-3 py-2.5 rounded-lg text-base font-medium text-ink hover:bg-cream-300/40"
          >
            Contact
          </button>
          <div className="pt-2">
            <button
              onClick={() => scrollToSection("#contact")}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-plum hover:bg-plum-hover transition-colors"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
